import { forwardRef, useEffect, useState } from "react";
import { X, MapPin, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DefectCrop } from "@/components/defects/DefectCrop";
import { nz } from "@/components/defects/TaskDefects";
import { PeoplePicker, useAbsenceMap } from "@/components/shared/PeoplePicker";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return iso(d); };
// „Do pátku" = nejbližší pátek; v pátek a o víkendu ten příští.
const friday = () => { const d = new Date(); const add = ((5 - d.getDay() + 7) % 7) || 7; d.setDate(d.getDate() + add); return iso(d); };
const autoTitle = (ds) => (ds.length ? `Oprava: ${(ds[0].text || "").trim().slice(0, 60)}${ds.length > 1 ? ` (+${ds.length - 1})` : ""}` : "");
const autoDesc = (ds) => ds.map((d, k) => `${k + 1}. ${(d.text || "").trim()}`).join("\n");

// Špendlíky z fotek → NOVÝ úkol (178). 187: stejná pole jako Úkoly › Nový úkol
// (Název, Popis, Zakázka, Termín + Priorita, Přiřadit), navíc fotky se špendlíky.
// Název a popis se doplní z popisů závad, dokud je člověk sám nepřepíše.
// returnTask = { id, title }: fotky jsou z úkolu čekajícího na schválení → jde ho
// i Vrátit k opravě (závady se přidají k němu, 187).
export const DefectTaskForm = forwardRef(function DefectTaskForm(
  { drafts, setDrafts, projectId, projectName, assignee, sourceKind, sourceId, onEditDraft, onCreated, highlight = false, note, returnTask, bare = false }, ref) {
  const [deadline, setDeadline] = useState(tomorrow);
  const [priority, setPriority] = useState("Vysoká");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [touched, setTouched] = useState({ title: false, desc: false });
  const [who, setWho] = useState(assignee?.id ? [assignee.id] : []);
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState(false);
  const has = drafts.length > 0;

  useEffect(() => { if (has && !team.length) api.get("/team").then((r) => setTeam(r.data || [])).catch(() => {}); }, [has, team.length]);
  useEffect(() => { if (assignee?.id) setWho((w) => (w.length ? w : [assignee.id])); }, [assignee?.id]);
  useEffect(() => {
    if (!touched.title) setTitle(autoTitle(drafts));
    if (!touched.desc) setDesc(autoDesc(drafts));
  }, [drafts, touched.title, touched.desc]);
  const absences = useAbsenceMap(null, deadline || null, has);

  if (!has) return null;
  const missing = drafts.some((d) => !d.text.trim());
  const people = team.filter((u) => u.status !== "deactivated" && u.role !== "ACCOUNTANT");
  const toggle = (id) => setWho((w) => (w.includes(id) ? w.filter((x) => x !== id) : [...w, id]));
  const reset = () => { setDrafts([]); setTouched({ title: false, desc: false }); };

  const create = async () => {
    if (missing) return toast.error("Doplňte popis u každé závady.");
    if (!title.trim()) return toast.error("Vyplňte název úkolu.");
    setBusy(true);
    try {
      const { data } = await api.post("/defects/task", {
        project_id: projectId, assignee_ids: who, title: title.trim(), description: desc, priority,
        deadline: deadline || null, defects: drafts, source_kind: sourceKind, source_id: sourceId });
      toast.success(`Úkol vytvořen: ${data.title}`);
      reset();
      onCreated?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const giveBack = async () => {
    if (missing) return toast.error("Doplňte popis u každé závady.");
    setBusy(true);
    try {
      await api.post(`/tasks/${returnTask.id}/defects/return`, { defects: drafts, comment: null });
      toast.success(`Vráceno k opravě: ${returnTask.title}`);
      reset();
      onCreated?.({ returned: returnTask.id });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <section ref={ref} data-testid="defect-task-form"
      className={bare ? "space-y-3 pr-1" : `rounded-[18px] border-[1.5px] bg-card p-3.5 md:p-4 space-y-3 scroll-mt-4 scroll-mb-24 transition-shadow motion-reduce:transition-none ${highlight ? "border-brand shadow-[0_0_0_4px_rgba(224,112,63,.25)]" : "border-brand/50"}`}>
      <h3 className={`font-extrabold ${bare ? "text-lg pr-10" : "text-[15px]"}`}>Nový úkol · {drafts.length} {nz(drafts.length)} na fotkách</h3>
      <div className="divide-y divide-border">
        {drafts.map((d, k) => (
          <div key={k} className="grid grid-cols-[44px_1fr_auto] gap-2.5 items-center py-2">
            <DefectCrop url={d.photo_url} x={d.x} y={d.y} n={k + 1} tone="draft" size={44} onClick={() => onEditDraft?.(k)} />
            {d.text.trim() ? <span className="text-sm break-words min-w-0">{d.text}</span>
              : <button type="button" onClick={() => onEditDraft?.(k)} className="text-sm font-bold text-brand text-left">Doplnit popis…</button>}
            <button type="button" onClick={() => setDrafts((ds) => ds.filter((_, i) => i !== k))} aria-label="Odebrat závadu"
              className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary"><X size={15} /></button>
          </div>
        ))}
      </div>

      <div><Label>Název *</Label>
        <Input value={title} onChange={(e) => { setTitle(e.target.value); setTouched((t) => ({ ...t, title: true })); }}
          className="h-11 mt-1 w-full min-w-0" data-testid="defect-task-title" /></div>
      <div><Label>Popis</Label>
        <Textarea value={desc} onChange={(e) => { setDesc(e.target.value); setTouched((t) => ({ ...t, desc: true })); }}
          className="mt-1" rows={3} data-testid="defect-task-desc" /></div>
      {projectName && (
        <div><Label>Zakázka</Label>
          <div className="h-11 mt-1 px-3 rounded-md border border-input bg-background flex items-center text-sm truncate" data-testid="defect-task-project">{projectName}</div></div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0"><Label>Termín</Label>
          <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="h-11 mt-1 w-full min-w-0" data-testid="defect-task-deadline" />
          <div className="flex gap-3 mt-1.5">
            <button type="button" onClick={() => setDeadline(tomorrow())} className="text-[13px] font-semibold text-brand hover:underline" data-testid="defect-due-tomorrow">Zítra</button>
            <button type="button" onClick={() => setDeadline(friday())} className="text-[13px] font-semibold text-brand hover:underline" data-testid="defect-due-friday">Do pátku</button>
          </div>
        </div>
        <div className="min-w-0"><Label>Priorita</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="h-11 mt-1" data-testid="defect-task-priority"><SelectValue /></SelectTrigger>
            <SelectContent>{["Nízká", "Střední", "Vysoká"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Přiřadit</Label>
        <div className="mt-1">
          <PeoplePicker people={people} selected={who} onToggle={toggle} absences={absences} compact leadId={who[0]}
            sub={(u) => (u.id === assignee?.id ? "nafotil" : u.position || "")} testid="defect-assignee-picker" maxH="max-h-52" />
        </div>
        {who.length > 1 && <p className="text-[11px] text-muted-foreground mt-1">První vybraný odpovídá — dostane upozornění a odškrtává hotovo.</p>}
      </div>

      <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
        {returnTask && (
          <Button variant="outline" onClick={giveBack} disabled={busy || missing} data-testid="defect-task-return"
            className="h-11 w-full md:w-auto rounded-full font-bold"><Undo2 size={16} /> Vrátit k opravě</Button>
        )}
        <Button onClick={create} disabled={busy || missing} data-testid="defect-task-create"
          className="h-11 w-full md:w-auto rounded-full font-extrabold px-6 bg-brand hover:bg-brand/90 text-white"><MapPin size={16} /> Vytvořit úkol</Button>
      </div>
      {returnTask && <p className="text-[12px] text-muted-foreground">Fotky jsou z úkolu „{returnTask.title}“, který čeká na schválení — můžeš ho rovnou vrátit k opravě.</p>}
      {note && <p className="text-[12px] text-muted-foreground">{note}</p>}
    </section>
  );
});
